import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useOperation } from '@/hooks/api/useOperation'
import { OperationTimeline } from '@/components/operations/OperationTimeline'
import { StatusBadge } from '@/components/operations/StatusBadge'
import { KindBadge } from '@/components/operations/KindBadge'
import { TxHashLink } from '@/components/operations/TxHashLink'
import { chainName } from '@/config/chains'
import { formatTimestamp } from '@/lib/utils'

export const Route = createFileRoute('/operations/$id')({
    component: OperationDetail,
})

function OperationDetail() {
    const { id } = Route.useParams()
    const { data, isLoading, isError, error } = useOperation(id)

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Operation</h1>
                <p className="text-muted-foreground font-mono text-sm">{id}</p>
            </div>

            {isLoading && <Skeleton className="h-32 w-full" />}
            {isError && (
                <Card>
                    <CardContent className="text-destructive py-6 text-sm">
                        Could not load operation:{' '}
                        {error instanceof Error ? error.message : 'unknown error'}
                    </CardContent>
                </Card>
            )}

            {data && (
                <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle>Status</CardTitle>
                            <CardDescription>Live-polled until terminal.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center gap-2">
                                <StatusBadge status={data.status} /> <KindBadge kind={data.kind} />
                            </div>
                            <OperationTimeline status={data.status} />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Details</CardTitle>
                            <CardDescription>Chain + transaction metadata.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                            <Row label="Chain">
                                {chainName(data.chainId)} ({data.chainId})
                            </Row>
                            <Row label="Attempt">{data.attempt}</Row>
                            <Row label="Tx hash">
                                <TxHashLink chainId={data.chainId} txHash={data.txHash} />
                            </Row>
                            <Row label="Block">{data.blockNumber ?? '—'}</Row>
                            <Row label="Receipt status">{data.receiptStatus ?? '—'}</Row>
                            <Row label="Created">{formatTimestamp(data.createdAt)}</Row>
                            <Row label="Updated">{formatTimestamp(data.updatedAt)}</Row>
                            {data.error && (
                                <div className="border-destructive/40 bg-destructive/10 text-destructive rounded border p-2 text-xs">
                                    {data.error}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">{label}</span>
            <span className="text-foreground">{children}</span>
        </div>
    )
}
